import moment from 'moment'
import { TimelineItemTypes } from 'consts/timelineConsts'

/**
 * A cycle's own dates are AWSDate - `2021-09-05`, a calendar day with no zone - while `createdAt`,
 * `updatedAt` and the rest are AWSDateTime instants. Moment reads the first as local midnight and
 * the second as a point in time, so comparing them directly compares two different things: east of
 * UTC a treatment stamped at UTC midnight looks *later* than a cycle starting that same calendar
 * day, the cadence appears to begin after the cycle it governs, and the cycle is skipped. Missed
 * scans then go undrawn, silently, on a clinical chart.
 *
 * Everything stored is therefore read through here: the calendar day the backend meant, which for
 * an instant is its day in UTC, expressed as local midnight so the dates still render as the days
 * they are.
 */
const calendarDay = value => moment(moment.utc(value).format('YYYY-MM-DD'))

/**
 * Today, as the doctor reading the chart experiences it.
 *
 * Deliberately the local day rather than the UTC one: at 8pm in New York the UTC day has already
 * rolled over, and taking it would call a scan due tomorrow missed today.
 */
const today = now => moment(now).startOf('day')

/**
 * Cycles whose `originalEndDate` was pushed past this year carry the "scan cycles disabled"
 * sentinel the backend writes when the patient moves to a status with `scanCyclesEnabled: false`.
 * It writes the year 2077; the threshold is loose so a change of sentinel does not slip through.
 */
const DISABLED_CYCLE_SENTINEL_YEAR = 2070
/**
 * A cycle outside this range cannot be trusted as a recurrence.
 *
 * The shortest scan interval the product offers is 5 days, so anything under that is an artifact -
 * usually a cycle abandoned the day it was created. Reading one of those as a recurrence turns a
 * patient who stopped scanning in 2021 into two thousand missed scans. At the other end, a span of
 * more than a year means `originalEndDate` was reset mid-cycle (status change, interval change,
 * practice-set next scan date) and no longer describes what was expected.
 */
const MIN_TRUSTED_CYCLE_LENGTH_DAYS = 5
const MAX_TRUSTED_CYCLE_LENGTH_DAYS = 400
/**
 * Beyond this, a single cycle's missed scans are collapsed into one counted marker.
 *
 * The bar is deliberately high. A gap is drawn across every month it spans, so even a year and a
 * half of missed weekly scans works out at a handful per month, which the axis has room for - and
 * collapsing a long gap into a single marker hides the very thing the feature exists to show.
 */
export const MAX_MISSED_MARKERS_PER_CYCLE = 200

const PausedCycleStatus = 'paused'
/**
 * The writes a cycle takes in a life nothing interfered with: one to create it, and for a closed
 * one, a second to close it. Anything beyond that moved its due date - a practice setting a next
 * scan date, a change of interval or status, or a pause and resume, which rewrites the due date too.
 */
const WRITES_TO_CREATE_A_CYCLE = 1
const WRITES_TO_CLOSE_A_CYCLE = 1
/** Used only when `_version` is missing, and then only for an open cycle, whose writes all move it. */
const UNTOUCHED_CYCLE_TOLERANCE_HOURS = 24
/**
 * The intervals the product offers, in days.
 *
 * A recorded interval landing within a day of one of these is snapped to it. Two things put it a
 * day or two out: the cycle's start and its due date are written in different zones (the lambda's
 * UTC for one, the patient's for the other), and the day-granularity of both. Snapping absorbs that
 * without inventing precision - a genuinely custom interval, which is equidistant from two of these
 * or far from all of them, is left as recorded.
 */
const PRODUCT_INTERVAL_DAYS = [5, 7, 10, 14, 21, 28, 30, 42, 45, 90, 180, 365]
const INTERVAL_SNAP_TOLERANCE_DAYS = 1

/** The treatment's interval, in days. */
const getTreatmentIntervalInDays = treatment => {
  if (!treatment?.cycleInterval) {
    return null
  }

  const days = moment.duration(treatment.cycleInterval, treatment.cycleIntervalPeriodType || 'day').asDays()
  return days >= MIN_TRUSTED_CYCLE_LENGTH_DAYS && days <= MAX_TRUSTED_CYCLE_LENGTH_DAYS ? Math.round(days) : null
}

/** The nearest interval the product offers, when the recorded one is within a day of exactly one. */
const snapToProductInterval = days => {
  if (PRODUCT_INTERVAL_DAYS.includes(days)) {
    return days
  }

  const near = PRODUCT_INTERVAL_DAYS.filter(interval => Math.abs(interval - days) <= INTERVAL_SNAP_TOLERANCE_DAYS)
  return near.length === 1 ? near[0] : days
}

/**
 * Whether a cycle still records the interval it was created with.
 *
 * Counted in writes: a cycle written more times than its life needs has had its due date moved, and
 * its span then straddles the change rather than describing an interval. Across 2,373 closed
 * staging cycles, 77% of those written exactly twice record an interval the product actually
 * offers, against 28% of those written more often.
 *
 * A cycle that records a product interval is trusted whatever its write count, since the number it
 * holds is one a practice could have chosen; that rescues the ordinary cycle carrying an extra
 * write from some unrelated migration.
 */
const isCycleUntouched = cycle => {
  const expectedWrites = WRITES_TO_CREATE_A_CYCLE + (cycle.actualEndDate ? WRITES_TO_CLOSE_A_CYCLE : 0)

  if (cycle._version) {
    return cycle._version <= expectedWrites || PRODUCT_INTERVAL_DAYS.includes(getRecordedInterval(cycle))
  }

  // Without a version, only an open cycle can be told apart: a closed one's last write is its close.
  return (
    !!cycle.actualEndDate ||
    !cycle.updatedAt ||
    !cycle.createdAt ||
    moment(cycle.updatedAt).diff(moment(cycle.createdAt), 'hours') <= UNTOUCHED_CYCLE_TOLERANCE_HOURS
  )
}

/**
 * The interval a cycle recorded, before any trust is applied.
 *
 * A cycle opened by a scan starts the day *after* it (`startDate = today + 1`) while its due date is
 * set a whole interval out (`originalEndDate = today + interval`), so its span is a day short of the
 * interval. A patient's first cycle is not opened by a scan and starts the same day, so its span is
 * the gap as written. Both are then snapped to a product interval where one is within a day, which
 * absorbs the zone skew between the two fields.
 */
const getRecordedInterval = cycle => {
  const start = cycle.startDate || cycle.createdAt
  if (!start || !cycle.originalEndDate) {
    return null
  }

  const isFirstCycle = Number(cycle.index) === 0
  const span = calendarDay(cycle.originalEndDate).diff(calendarDay(start), 'days')

  return snapToProductInterval(span + (isFirstCycle ? 0 : 1))
}

/**
 * The interval the cycle itself recorded, in days, or `null` when it cannot be trusted as one.
 *
 * This is the interval that was in force *at the time*, which is why missed scans are derived from
 * it rather than from `Treatment.cycleInterval` - the treatment only holds the current setting, and
 * replaying that setting backwards produces the wrong answer for any patient whose interval changed.
 */
export const getCycleLengthInDays = cycle => {
  const interval = getRecordedInterval(cycle)

  return interval >= MIN_TRUSTED_CYCLE_LENGTH_DAYS && interval <= MAX_TRUSTED_CYCLE_LENGTH_DAYS ? interval : null
}

/**
 * How often a scan was expected, as it changed over time.
 *
 * Nothing records the history of the interval directly: `Treatment.cycleInterval` holds only the
 * current setting and `Cycle.originalEndDate` is overwritten in place. What can be recovered is that
 * each cycle captured the interval in force when it was created, which gives the interval by period
 * wherever scans happened - seven days until May and fourteen after - rather than one interval
 * replayed over everything.
 *
 * The treatment contributes a final segment, but a weak one: its row is rewritten on every scan
 * upload, so `updatedAt` is the last scan for anyone still scanning, and only dates an interval
 * change for a patient who has stopped - which is the case this view exists for.
 *
 * @returns `[{ from, lengthDays }]`, earliest first.
 */
export const buildCadenceTimeline = ({ cycles = [], treatment = null } = {}) => {
  const fromCycles = cycles
    // A patient's first cycle is not opened by a scan, and its due date is when the first scan is
    // wanted - today, the booked date, or a year out for ortho - never an interval.
    .filter(cycle => !cycle._deleted && cycle.index != null && Number(cycle.index) !== 0 && isCycleUntouched(cycle))
    .map(cycle => ({ from: calendarDay(cycle.startDate || cycle.createdAt), lengthDays: getCycleLengthInDays(cycle) }))
    .filter(segment => segment.lengthDays && segment.from.isValid())

  const treatmentIntervalDays = getTreatmentIntervalInDays(treatment)
  const fromTreatment =
    treatmentIntervalDays && treatment.updatedAt
      ? [{ from: calendarDay(treatment.updatedAt), lengthDays: treatmentIntervalDays }]
      : []

  return [...fromCycles, ...fromTreatment].sort((a, b) => a.from.valueOf() - b.from.valueOf())
}

/**
 * How often a scan was expected on a given date, according to the cadence timeline.
 *
 * A date nothing covers returns nothing. Reaching for the earliest interval known and applying it
 * backwards is the move this module exists to avoid: a patient who scanned monthly in 2023 and moved
 * to weekly in 2026 would have 2023 walked at a week, manufacturing four times the real misses.
 */
export const getCadenceAt = (cadenceTimeline = [], date) => {
  const applicable = cadenceTimeline.filter(segment => segment.from.isSameOrBefore(date))

  return applicable.length ? applicable[applicable.length - 1].lengthDays : null
}

/**
 * A cycle can only tell us about missed scans when it recorded a due date we can trust.
 */
export const isCycleMeasurable = (cycle, startingCadence = null) =>
  !!cycle &&
  !cycle._deleted &&
  !!cycle.originalEndDate &&
  cycle.status !== PausedCycleStatus &&
  calendarDay(cycle.originalEndDate).year() < DISABLED_CYCLE_SENTINEL_YEAR &&
  !!(getCycleLengthInDays(cycle) || startingCadence)

/**
 * How many scans a single cycle missed, and when each one was due.
 *
 * A cycle ends when a scan closes it, so a patient who skips scans leaves behind one cycle that ran
 * long rather than one cycle per skipped scan. We recover the skipped ones by stepping the cycle's
 * own length forward from its due date.
 *
 * A closed cycle ends with a scan, and that scan answers the most recent due date before it - late,
 * but not missed - so only the due dates before that one count. Nothing has answered an open cycle,
 * so every due date that has already passed counts, including the one the patient is overdue on
 * right now. That last one is what shows after a patient's most recent scan.
 *
 * @param cycle the Cycle entity.
 * @param now reference point for a cycle that is still open.
 * @returns `[]`, or the dates the missed scans were due, earliest first.
 */
const walkMissedDueDates = (cycle, now, cadenceTimeline) => {
  const none = { dueDates: [], total: 0, lastDueDate: null }

  const cycleStart = calendarDay(cycle.startDate || cycle.createdAt)
  const startingCadence = getCadenceAt(cadenceTimeline, cycleStart)

  if (!isCycleMeasurable(cycle, startingCadence)) {
    return none
  }

  // A cycle that still records the interval it was created with is read from its own due date. One
  // whose due date has since been moved no longer lines up with its start, so it is walked from its
  // start at whatever interval was in force as each due date came around.
  //
  // Either way the walk *steps* at the interval in force as each due date came around, not at the
  // one the cycle opened with - a cycle can outlive a change of cadence.
  //
  // A first cycle keeps its due date - that is the date the first scan was wanted - but not its
  // span as a stride, for the same reason it contributes no cadence: the gap to a booked first scan,
  // or the year an ortho patient is given, says nothing about how often to scan afterwards. It also
  // keeps the *current* date rather than the planned one: re-booking a first scan moves when it was
  // wanted, which is not the same as skipping it.
  const isFirstCycle = Number(cycle.index) === 0
  const ownLength = !isFirstCycle && isCycleUntouched(cycle) ? getCycleLengthInDays(cycle) : null
  if (!ownLength && !startingCadence) {
    return none
  }

  const closedAt = cycle.actualEndDate ? calendarDay(cycle.actualEndDate) : today(now)
  const anchor =
    ownLength || isFirstCycle ? calendarDay(cycle.originalEndDate) : moment(cycleStart).add(startingCadence, 'days')

  const dueDates = []
  let total = 0
  let dueDate = anchor
  let lastDueDate = null
  let previousDueDate = null

  const isWithinRun = date => (cycle.actualEndDate ? date.isSameOrBefore(closedAt) : date.isBefore(closedAt))

  while (isWithinRun(dueDate)) {
    total += 1
    previousDueDate = lastDueDate
    lastDueDate = moment(dueDate)
    // Past the point where they are drawn one by one only the count and the end of the run matter,
    // so the dates stop being kept - a cycle abandoned for years would otherwise build an array of
    // thousands.
    if (total <= MAX_MISSED_MARKERS_PER_CYCLE + 1) {
      dueDates.push(moment(dueDate))
    }

    // The interval in force as each due date comes around, falling back to the one this cycle was
    // opened with. A cycle left open across a cadence change would otherwise step at its opening
    // interval the whole way: a 7 day cycle running into a 28 day schedule drew 29 markers where 9
    // were owed, and the reverse direction lost real misses.
    const step = getCadenceAt(cadenceTimeline, dueDate) || ownLength
    if (!step) {
      break
    }

    dueDate = moment(dueDate).add(step, 'days')
  }

  // The scan that closes a cycle answers the most recent due date before it - late, but not missed.
  // Nothing has answered an open cycle, so every due date already passed counts. The end of the run
  // is tracked apart from the dates that are kept, so a run long enough to be collapsed still
  // reports where it actually ended.
  return cycle.actualEndDate
    ? { dueDates: dueDates.slice(0, -1), total: Math.max(total - 1, 0), lastDueDate: previousDueDate }
    : { dueDates, total, lastDueDate }
}

export const getMissedDueDates = (cycle, now = moment(), cadenceTimeline = []) =>
  walkMissedDueDates(cycle, now, cadenceTimeline).dueDates

/**
 * Builds the missed scan items the time-based mini timeline renders, from the patient's cycles.
 *
 * Shaped like the TimelineItems the rest of the timeline works with (`id`, `type`, `eventDate`,
 * `payload`) so the layout can treat them as ordinary items.
 *
 * @param cycles the patient's Cycle entities, in any order.
 * @param now reference point for the open cycle.
 * @returns missed scan items, earliest first.
 */
export const buildMissedScanItems = ({ cycles = [], treatment = null, now = moment() } = {}) => {
  const cadenceTimeline = buildCadenceTimeline({ cycles, treatment })

  return cycles
    .flatMap(cycle => {
      const { dueDates: missedDueDates, total, lastDueDate } = walkMissedDueDates(cycle, now, cadenceTimeline)
      if (!missedDueDates.length) {
        return []
      }

      if (total > MAX_MISSED_MARKERS_PER_CYCLE) {
        const firstDueDate = missedDueDates[0]

        return [
          {
            id: `missed-${cycle.id}-collapsed`,
            type: TimelineItemTypes.MissedScan,
            // At the date the gap opens, which is the part a practice can still act on.
            eventDate: firstDueDate.toISOString(),
            payload: {
              cycleId: cycle.id,
              missedCount: total,
              fromDate: firstDueDate.toISOString(),
              toDate: lastDueDate.toISOString(),
              isCollapsed: true
            }
          }
        ]
      }

      return missedDueDates.map((dueDate, i) => ({
        id: `missed-${cycle.id}-${i}`,
        type: TimelineItemTypes.MissedScan,
        eventDate: dueDate.toISOString(),
        payload: {
          cycleId: cycle.id,
          missedCount: 1,
          fromDate: dueDate.toISOString(),
          toDate: dueDate.toISOString(),
          isCollapsed: false
        }
      }))
    })
    .sort((a, b) => moment(a.eventDate).valueOf() - moment(b.eventDate).valueOf())
}
